'use client';

import { useEffect, useMemo, useState } from 'react';

import { useInviteMembers } from '@/features/workspaces/hooks';
import { inviteMemberSchema } from '@/features/workspaces/schemas';
import { useAppSelector } from '@/store/hooks';
import { ROLE_VALUES } from '@web/constants';
import { useWizardBrowserStorage } from '@web/hooks';
import { MainButton, SmartInput } from '@web/ui';

import type { InviteMemberPayload } from '@/features/workspaces/types';

type Props = {
  onSuccess?: () => void;
  showOnboardingIntro?: boolean;
};

const onboardingIntroCopy =
  'Invite your team members - Add the people who will plan, manage, and deliver the work in this workspace. Once they join, they can start collaborating in the right roles from day one.' as const;

export function InviteMembersForm({
  onSuccess,
  showOnboardingIntro = false,
}: Props) {
  const { currentUser, currentWorkspaceSlug } = useAppSelector((s) => s.auth);
  const wizardId = `${currentUser?.email || 'anon'}:${currentWorkspaceSlug || 'no-ws'}`;
  const wizard = useWizardBrowserStorage({
    wizardId,
    version: 1,
    storage: 'local',
  });

  const persisted = wizard.getStepData<InviteMemberPayload[]>('invites');
  const [forms, setForms] = useState<InviteMemberPayload[]>(
    () =>
      persisted ?? [
        { invitee_email: '', message: '', role_slug: 'member' },
        { invitee_email: '', message: '', role_slug: 'member' },
        { invitee_email: '', message: '', role_slug: 'member' },
      ],
  );

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const { inviteAll, isLoading } = useInviteMembers();

  const isRowEmpty = (r: InviteMemberPayload) =>
    r.invitee_email.trim() === '' && r.message.trim() === '';

  const isValid = useMemo(() => {
    return forms.every(
      (f) => isRowEmpty(f) || inviteMemberSchema.safeParse(f).success,
    );
  }, [forms]);

  const setField = <K extends keyof InviteMemberPayload>(
    index: number,
    key: K,
    value: InviteMemberPayload[K],
  ) => {
    setForms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value } as InviteMemberPayload;
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    const toSend = forms.filter((f) => !isRowEmpty(f));

    const res = await inviteAll(toSend);

    if (res.success) {
      onSuccess?.();
    }
  };

  const handleSkip = () => {
    onSuccess?.();
  };

  useEffect(() => {
    const t = setTimeout(() => {
      wizard.saveStep('invites', forms);
    }, 400);
    return () => clearTimeout(t);
  }, [forms, wizard]);

  const roleOptions = ROLE_VALUES.map((r) => ({
    value: r,
    label: r.charAt(0).toUpperCase() + r.slice(1),
  }));

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'grid',
        gap: '1rem',
      }}
    >
      {showOnboardingIntro ? (
        <div className="card-container-glass">{onboardingIntroCopy}</div>
      ) : null}

      <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0.5rem' }}>
        {forms.map((f, idx) => (
          <div
            key={`invite-${idx}`}
            style={{
              display: 'grid',
              gap: '1rem',
              gridTemplateColumns: '2fr 1fr 2fr',
              alignItems: 'start',
              marginBottom: '1rem',
            }}
          >
            <SmartInput
              type="text"
              name={`invitee_email-${idx}`}
              label={`Invitee Email ${idx + 1}`}
              value={f.invitee_email}
              onChange={(v) => setField(idx, 'invitee_email', v)}
              onValidateChange={(_, err) =>
                setErrors((e) => ({
                  ...e,
                  [`${idx}.invitee_email`]: err || undefined,
                }))
              }
              schema={
                isRowEmpty(f)
                  ? undefined
                  : inviteMemberSchema.shape.invitee_email
              }
              error={errors[`${idx}.invitee_email`]}
            />

            <SmartInput
              type="select"
              name={`role_slug-${idx}`}
              label="Role"
              options={roleOptions}
              value={f.role_slug}
              onChange={(v) => setField(idx, 'role_slug', v as string)}
              onValidateChange={(_, err) =>
                setErrors((e) => ({
                  ...e,
                  [`${idx}.role_slug`]: err || undefined,
                }))
              }
              schema={inviteMemberSchema.shape.role_slug}
              error={errors[`${idx}.role_slug`]}
            />

            <SmartInput
              type="textarea"
              name={`message-${idx}`}
              label="Message (optional)"
              value={f.message}
              onChange={(v) => setField(idx, 'message', v)}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
        <MainButton
          type="button"
          variant="info"
          onClick={() =>
            setForms((prev) => [
              ...prev,
              { invitee_email: '', message: '', role_slug: 'member' },
            ])
          }
        >
          Add Invite
        </MainButton>
        <MainButton type="submit" isLoading={isLoading} disabled={!isValid}>
          Send Invites
        </MainButton>
        <MainButton type="button" onClick={handleSkip}>
          Skip
        </MainButton>
      </div>
    </form>
  );
}
